import React, { useEffect, useRef, useState } from "react";
import { Sun, Moon, Monitor, Check } from "lucide-react";
import { applyTheme, getThemePref, resolveDark } from "../lib/theme";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const OPTIONS = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "system", label: "System", icon: Monitor },
];

// Subtle theme switcher: icon button that opens a small Light/Dark/System menu.
export default function ThemeSwitcher({ align = "right", position = "up", className = "" }) {
  const { user, updateUser } = useAuth();
  const [pref, setPref] = useState(() => getThemePref());
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const choose = (key) => {
    applyTheme(key);
    setPref(key);
    setOpen(false);
    if (user) {
      const next = { ...(user.settings || {}), theme: key };
      updateUser({ settings: next });
      api.put("/auth/profile", { settings: next }).catch(() => {});
    }
  };

  const Current = resolveDark(pref) ? Moon : Sun;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        data-testid="theme-switcher-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Change theme"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#636E72] transition-colors hover:bg-[#A7C4A0]/15 hover:text-[#0F766E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F766E]/40"
        title="Theme"
      >
        <Current className="h-[18px] w-[18px]" strokeWidth={1.7} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="theme-switcher-menu"
          className={`absolute z-50 w-40 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-1 shadow-[0_8px_30px_rgba(45,52,54,0.12)] ${position === "down" ? "top-full mt-2" : "bottom-full mb-2"} ${align === "right" ? "right-0" : "left-0"}`}
        >
          {OPTIONS.map((o) => {
            const active = pref === o.key;
            return (
              <button
                key={o.key}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                data-testid={`theme-switcher-${o.key}`}
                onClick={() => choose(o.key)}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${active ? "bg-[#0F766E]/8 text-[#0F766E]" : "text-[#636E72] hover:bg-[#A7C4A0]/12"}`}
              >
                <o.icon className="h-4 w-4" strokeWidth={1.7} />
                <span className="flex-1 text-left">{o.label}</span>
                {active && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
